import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useLocation} from 'react-router';

import {
  getAddressFromInputValue,
  IAddressFormErrors,
  ParsedAddress,
  parseGooglePlaceToAddress,
  validateAddressForm,
} from '@/app/utils/AddressUtils/AddressUtils';
import {ENV} from '@/constants/EnvConstants';
import {IPublicRequestCheckoutUrl} from '@/dataSource/src/api/payment-link/entities';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {usePaymentLinkRepository} from '@/dataSource/src/api/payment-link/useRepository';
import {useCountryCode} from '@/hooks';
import useRiskScriptV2 from '@/hooks/Risk/useRiskV2';
import logError from '@/utils/errors/logError';

import {usePaymentLinkViewModelProvider} from '../../ViewModelContext';

// Mock for Google Places service
const loadGooglePlaces = (): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, 1000));

const fileName = 'useActions PaymentLinkForm';

export interface IAddressFormPaymentLink {
  firstName: string;
  lastName: string;
  email: string;
  phone_number: string;
  addressLineShortened: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  region: string;
}

export const useActions = ({amount, selectedFee, isSelectTabAmount}) => {
  const autoCompletePaymentRef = useRef(null);
  const {handlePostCheckoutLink} = usePaymentLinkRepository();
  const {isCountryCodeVisible} = useCountryCode();

  // States
  const [formAddress, setFormAddressState] = useState<IAddressFormPaymentLink>({
    firstName: '',
    lastName: '',
    email: '',
    phone_number: '+1',
    addressLineShortened: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    region: '',
  });
  const [emailError, setEmailError] = useState<boolean>(false);
  const location = useLocation();
  const paymentLinkId = location.pathname.replace('/payment-link/', '');
  const [isGooglePlacesReady, setIsGooglePlacesReady] =
    useState<boolean>(false);
  const [isShowTermsAndConditionsModal, setIsShowTermsAndConditionsModal] =
    useState<boolean>(true);
  // Helper to update form state
  const [lastSelectedAddress, setLastSelectedAddress] =
    useState<ParsedAddress>(null);
  const {deviceSessionId} = useRiskScriptV2(ENV.VITE_RISK_PUBLIC_KEY);
  const {paymentLinkData, handleContinueFormCompleted} =
    usePaymentLinkViewModelProvider();
  const {trackSelectRequestMoneyPaymentConfirmationTermsConditions} =
    usePaymentLinkTrackers();

  const setFormAddress = useCallback(
    (updates: Partial<IAddressFormPaymentLink>) => {
      setFormAddressState(prev => ({...prev, ...updates}));
    },
    [],
  );

  const handleAddressChange = useCallback(
    (address: ParsedAddress) => {
      setLastSelectedAddress(address);
      setFormAddress({
        addressLine1: address.addressLine1 || '',
        addressLineShortened: address.addressLineShortened || '',
        city: address.city || '',
        zipCode: address.zipCode || '',
        state: address.state || '',
        region: address.region || '',
      });
    },
    [setFormAddress],
  );

  const handlePlaceChanged = useCallback(() => {
    if (autoCompletePaymentRef.current) {
      const place = autoCompletePaymentRef.current.getPlace();
      const addressData = parseGooglePlaceToAddress(place);
      handleAddressChange(addressData);
    }
  }, [handleAddressChange]);

  const handleSetValue = useCallback(
    (value: string) => {
      setFormAddress({addressLine1: value});
      if (!lastSelectedAddress) return;

      const address = getAddressFromInputValue(value, lastSelectedAddress);

      setFormAddress({
        addressLineShortened: address.addressLineShortened,
        city: address.city,
        state: address.state,
        region: address.region,
        zipCode: address.zipCode,
      });
    },
    [lastSelectedAddress, setFormAddress],
  );

  const getRedirectUrl = useCallback((): string => {
    const hostName = window.location.origin;
    const path = '/payment-link-outcome';
    return `${hostName}${path}`;
  }, []);

  const handleOnPostCheckoutLink = useCallback(
    async (body: IPublicRequestCheckoutUrl) => {
      try {
        const result = await handlePostCheckoutLink(body);
        if (result?.link) window.location.href = result.link;
      } catch (error) {
        logError(error, {
          customTitle: 'PaymentLinkForm - Error posting checkout link',
          fileName: fileName,
          functionName: 'handleOnPostCheckoutLink',
        });
      }
    },
    [handlePostCheckoutLink],
  );

  const onButtonPress = useCallback(async () => {
    const body: IPublicRequestCheckoutUrl = {
      amount,
      type: selectedFee.type,
      request_money_id: paymentLinkId,
      risk: {
        device_session_id: deviceSessionId,
      },
      return_url: getRedirectUrl(),
      payer: {
        email: formAddress.email,
        phone_number: formAddress.phone_number,
        first_name: formAddress.firstName,
        last_name: formAddress.lastName,
        address: {
          line1: formAddress.addressLineShortened,
          line2: formAddress.addressLine2,
          city: formAddress.city,
          state: formAddress.region,
          postal_code: formAddress.zipCode,
          country: 'US',
        },
      },
    };
    handleContinueFormCompleted(selectedFee);

    await handleOnPostCheckoutLink(body);
  }, [
    amount,
    selectedFee,
    paymentLinkId,
    deviceSessionId,
    getRedirectUrl,
    formAddress,
    handleContinueFormCompleted,
    handleOnPostCheckoutLink,
  ]);

  const disableButton = useMemo(() => {
    const {
      firstName,
      lastName,
      email,
      phone_number,
      addressLine1,
      city,
      state,
      zipCode,
      addressLineShortened,
    } = formAddress;

    // Validate form fields
    const isFormInvalid =
      !addressLineShortened ||
      !addressLine1 ||
      !city ||
      !state ||
      !zipCode ||
      !firstName ||
      !lastName ||
      !email ||
      !phone_number ||
      emailError;

    return isFormInvalid;
  }, [emailError, formAddress]);

  const addressFormErrors: IAddressFormErrors = useMemo(
    () => validateAddressForm(formAddress, !!lastSelectedAddress),
    [formAddress, lastSelectedAddress],
  );

  // Handle Google Places loading
  useEffect(() => {
    const loadPlaces = async () => {
      try {
        await loadGooglePlaces();
        setIsGooglePlacesReady(true);
      } catch (error) {
        logError(error, {
          customTitle: 'PaymentLinkForm - Error loading Google Places',
          fileName: fileName,
          functionName: 'loadGooglePlaces useEffect',
        });
      }
    };

    loadPlaces();
  }, []);

  const handleTrackTermsClick = useCallback(() => {
    if (!paymentLinkData || !selectedFee) return;

    trackSelectRequestMoneyPaymentConfirmationTermsConditions({
      receiver: {
        id: paymentLinkData.receiver_user.id,
        name: paymentLinkData.receiver_user.name,
      },
      single_payment: paymentLinkData.single_payment,
      id: paymentLinkId ?? '',
      information: {
        note: Boolean(paymentLinkData.note),
        gif: Boolean(paymentLinkData.gif),
      },
      optionSelected: {
        amount: amount.toString(),
        fee: selectedFee.flat_fee.toString(),
        type: selectedFee.type,
        prefill: isSelectTabAmount,
      },
    });
  }, [
    paymentLinkData,
    selectedFee,
    trackSelectRequestMoneyPaymentConfirmationTermsConditions,
    paymentLinkId,
    amount,
    isSelectTabAmount,
  ]);

  return {
    formAddress,
    setFormAddress,
    isGooglePlacesReady,
    handleAddressChange,
    handlePlaceChanged,
    handleSetValue,
    autoCompletePaymentRef,
    disableButton,
    isShowTermsAndConditionsModal,
    setIsShowTermsAndConditionsModal,
    onButtonPress,
    addressFormErrors,
    handleTrackTermsClick,
    setEmailError,
    isCountryCodeVisible,
  };
};
